import { useState } from 'react';
import { Box, Group, Text } from '@mantine/core';
import { useMakeDefaultTemplate } from 'src/data-capture/hooks/templates/useMakeDefaultTemplate';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { Modal } from 'src/design-system/Modal';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type DefaultCandidate = Pick<TemplateWithoutSections, 'id' | 'displayName' | 'isDefault'>;

type Props = {
  template: DefaultCandidate | undefined;
  onClose: () => void;
};

export function MakeDefaultTemplateModal({ template, onClose }: Props) {
  const { makeDefault, isMakingDefault } = useMakeDefaultTemplate();

  // Keeps the name on screen while the modal fades out after `template` is cleared.
  const [shownTemplate, setShownTemplate] = useState(template);
  if (template && template !== shownTemplate) {
    setShownTemplate(template);
  }
  const name = shownTemplate?.displayName ?? '';

  const handleConfirm = async () => {
    if (!template) return;
    if (await makeDefault(template)) {
      onClose();
    }
  };

  return (
    <Modal opened={!!template} onClose={onClose} title={`Make ${name} your default?`} size={440}>
      <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.9" mt="xs">
        {`From your next recorded meeting, we'll write notes automatically using ${name}. This applies to everyone at your firm.`}
      </Text>
      <WrittenNotesWontChange />
      <Group justify="flex-end" gap="xs" mt="lg">
        <Button variant="secondary" size="md" onClick={onClose} disabled={isMakingDefault}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={handleConfirm} loading={isMakingDefault}>
          Make default
        </Button>
      </Group>
    </Modal>
  );
}

export function WrittenNotesWontChange() {
  return (
    <Box bg="gray.0" px="sm" py="xs" mt="md" style={{ borderRadius: 8 }}>
      <Text size="sm" c="gray.7">
        {"Notes that have already been written won't change."}
      </Text>
    </Box>
  );
}
